import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FileText, Clock, CheckCircle2, AlertTriangle, Plus, FileDown,
  DollarSign, CalendarRange, BarChart3, ClipboardList,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ReportBuilderDrawer } from './ReportBuilder/ReportBuilderDrawer';
import { useGerarRelatorioPdf } from '@/hooks/useGerarRelatorioPdf';

interface Props {
  contratoId: string;
  contratoNome: string;
}

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ElementType }> = {
  gerado: { label: 'Gerado', variant: 'default', icon: CheckCircle2 },
  gerando: { label: 'Gerando', variant: 'secondary', icon: Clock },
  falha: { label: 'Falha', variant: 'destructive', icon: AlertTriangle },
};

const TIPO_LABELS: Record<string, string> = {
  mensal: 'Mensal',
  semanal: 'Semanal',
  executivo: 'Executivo',
  desvios: 'Desvios',
  earned_value: 'Earned Value',
};

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  try { return format(new Date(d), 'dd/MM/yyyy', { locale: ptBR }); } catch { return d; }
};

export const EmpRelatoriosView: React.FC<Props> = ({ contratoId, contratoNome }) => {
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selectedBoletim, setSelectedBoletim] = useState<string>('');
  const {
    gerarBoletimPdf, gerarMensalConsolidadoPdf, gerarResumoExecutivoPdf,
    gerarCronogramaPdf, gerarEvmPdf, gerarSaldosPdf, loading: pdfLoading,
  } = useGerarRelatorioPdf();

  const { data: boletins = [] } = useQuery({
    queryKey: ['emp-boletins-select', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_boletins_medicao')
        .select('id, numero_boletim, periodo_inicio, periodo_fim, status')
        .eq('contrato_id', contratoId)
        .order('numero_boletim', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!contratoId,
  });

  const { data: relatorios = [], isLoading } = useQuery({
    queryKey: ['emp-relatorios', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_relatorios')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!contratoId,
  });

  if (isLoading) {
    return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }

  const counts = {
    total: relatorios.length,
    gerado: relatorios.filter((r: any) => r.status === 'gerado').length,
    gerando: relatorios.filter((r: any) => r.status === 'gerando').length,
    falha: relatorios.filter((r: any) => r.status === 'falha').length,
  };

  return (
    <div className="space-y-6">
      {/* ── KPI Cards — full width */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Total</p><p className="text-2xl font-bold">{counts.total}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Gerados</p><p className="text-2xl font-bold text-primary">{counts.gerado}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Gerando</p><p className="text-2xl font-bold text-warning">{counts.gerando}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Falhas</p><p className="text-2xl font-bold text-destructive">{counts.falha}</p></CardContent></Card>
      </div>

      {/* ── Gerar PDFs — organized by category */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Gerar PDF por Tipo</h3>
          <Button onClick={() => setBuilderOpen(true)} size="sm" className="gap-2">
            <Plus className="h-4 w-4" /> Novo Relatório Personalizado
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Financeiro */}
          <Card className="border-l-4 border-l-primary">
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-primary" />
                <CardTitle className="text-sm">Financeiro</CardTitle>
              </div>
              <CardDescription className="text-xs">Medições, saldos e consolidados</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {/* Boletim de Medição */}
              <div className="flex items-center gap-2">
                <Select value={selectedBoletim} onValueChange={setSelectedBoletim}>
                  <SelectTrigger className="h-8 flex-1 text-xs">
                    <SelectValue placeholder="Selecionar boletim…" />
                  </SelectTrigger>
                  <SelectContent>
                    {boletins.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        #{b.numero_boletim} — {b.status}
                      </SelectItem>
                    ))}
                    {boletins.length === 0 && (
                      <SelectItem value="_none" disabled>Nenhum boletim</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline" size="sm" className="gap-1.5 shrink-0"
                  disabled={pdfLoading || !selectedBoletim}
                  onClick={() => selectedBoletim && gerarBoletimPdf(selectedBoletim, contratoId)}
                >
                  <FileDown className="h-3.5 w-3.5" /> Boletim
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1.5 flex-1" disabled={pdfLoading} onClick={() => gerarSaldosPdf(contratoId)}>
                  <FileDown className="h-3.5 w-3.5" /> Saldos Contratuais
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5 flex-1" disabled={pdfLoading} onClick={() => gerarMensalConsolidadoPdf(contratoId)}>
                  <FileDown className="h-3.5 w-3.5" /> Mensal Consolidado
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Planejamento & Performance */}
          <Card className="border-l-4 border-l-accent">
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center gap-2">
                <CalendarRange className="h-4 w-4 text-accent-foreground" />
                <CardTitle className="text-sm">Planejamento</CardTitle>
              </div>
              <CardDescription className="text-xs">Cronograma e indicadores EVM</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1.5 flex-1" disabled={pdfLoading} onClick={() => gerarCronogramaPdf(contratoId)}>
                  <FileDown className="h-3.5 w-3.5" /> Cronograma
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5 flex-1" disabled={pdfLoading} onClick={() => gerarEvmPdf(contratoId)}>
                  <BarChart3 className="h-3.5 w-3.5" /> Indicadores EVM
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Governança & Executivo */}
          <Card className="border-l-4 border-l-secondary">
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-secondary-foreground" />
                <CardTitle className="text-sm">Governança</CardTitle>
              </div>
              <CardDescription className="text-xs">Visão executiva e panorama geral</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <Button variant="outline" size="sm" className="gap-1.5 w-full" disabled={pdfLoading} onClick={() => gerarResumoExecutivoPdf(contratoId)}>
                <FileDown className="h-3.5 w-3.5" /> Resumo Executivo
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Relatorios table */}
      <Card>
        <CardHeader><CardTitle className="text-base">Relatórios — {contratoNome}</CardTitle></CardHeader>
        <CardContent className="p-0">
          {relatorios.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Nenhum relatório gerado para este contrato.</p>
              <p className="text-xs mt-1">Use o botão <strong>Novo Relatório</strong> para criar um relatório personalizado.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {relatorios.map((r: any) => {
                  const st = STATUS_MAP[r.status] || STATUS_MAP.gerado;
                  const StIcon = st.icon;
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.titulo}</TableCell>
                      <TableCell><Badge variant="outline">{TIPO_LABELS[r.tipo] || r.tipo}</Badge></TableCell>
                      <TableCell className="text-sm">{fmtDate(r.periodo_inicio)} — {fmtDate(r.periodo_fim)}</TableCell>
                      <TableCell>
                        <Badge variant={st.variant} className="flex items-center gap-1 w-fit">
                          <StIcon className="h-3 w-3" /> {st.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Report Builder Drawer */}
      <ReportBuilderDrawer
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        contratoId={contratoId}
        contratoNome={contratoNome}
      />
    </div>
  );
};
